<template>
  <div
    :id="modalId"
    class="modal fade"
    ref="modalRef"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header d-flex justify-content-between align-items-center">
          <h3 class="modal-title m-0">
            <strong>{{ title }}</strong>
          </h3>
          <button type="button" class="close btn-link" data-bs-dismiss="modal" :aria-label="$t('shared.close')">
            <span>&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <!-- Private item: prompt to make public -->
          <template v-if="isPrivate">
            <p class="mb-2">
              <strong>{{ $t('utils.copy_link_modal.this_item_type_label_is_private', { item_type_label: itemTypeLabel }) }}</strong>
            </p>
            <p class="mb-3 text-muted">
              <small>{{ $t('utils.copy_link_modal.you_need_to_make_this_item', { item_type_label: itemTypeLabel }) }}</small>
            </p>

            <!-- Email not confirmed: block with resend option -->
            <template v-if="!isEmailConfirmed">
              <div class="card p-3 mb-3">
                <small class="d-block mb-2">
                  {{ $t('utils.copy_link_modal.you_must_confirm_your_email_before', { item_type_label: itemTypeLabel }) }}
                </small>
                <button
                  class="btn btn-link p-0"
                  type="button"
                  @click.prevent="resendConfirmationEmail"
                  :disabled="resendLoading"
                >
                  <small>{{ resendLoading ? $t('utils.copy_link_modal.sending') : $t('users.settings.resend_confirmation_email') }}</small>
                </button>
                <small v-if="resendStatus" class="d-block text-muted mt-1">{{ resendStatus }}</small>
              </div>
            </template>

            <!-- Email confirmed: allow making public -->
            <template v-else>
              <button
                class="btn btn-primary"
                type="button"
                :disabled="makingPublic"
                @click="makePublicAndShare"
              >
                <span v-if="makingPublic" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('utils.copy_link_modal.make_public_amp_share') }}
              </button>
              <small v-if="makePublicError" class="d-block text-danger mt-2">{{ makePublicError }}</small>
            </template>
          </template>

          <!-- Public item: show share link -->
          <template v-else>
            <p class="mb-1">
              <strong>{{ label }}</strong>
              <span v-if="copied" class="text-yellow">{{ $t('utils.copy_link_modal.copied') }}</span>
            </p>
            <p class="mb-3">
              <small class="text-muted">{{ helperText }}</small>
            </p>

            <div class="input-group mb-3">
              <input
                ref="inputRef"
                class="form-control"
                type="text"
                :value="currentLink"
                readonly
                @focus="$event.target.select()"
              />
              <button class="btn btn-primary input-group-text" type="button" @click="copyToClipboard">
                <span class="material-icons">content_copy</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Modal } from 'bootstrap';
import graph_client from '../graphql/graph_client';
import { UpdateHikeDetails } from '../graphql/hikes-graph';
import { UpdateRecording } from '../graphql/recorded-trail-graph';
import { i18n } from '../../i18n';

const t = (key: string): string => i18n.global.t(key);

interface Props {
  modalId?: string;
  title?: string;
  label?: string;
  helperText?: string;
  defaultLink?: string;
}

const props = defineProps<Props>();

const modalRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const modalInstance = ref<Modal | null>(null);
const currentLink = ref<string>(props.defaultLink || window.location.href);
const copied = ref(false);

// Privacy-aware state
const isPrivate = ref(false);
const currentItem = ref<any>(null);
const currentItemType = ref<'hike' | 'recording' | null>(null);
const itemTypeLabel = ref(t('utils.copy_link_modal.noun_hike'));
const isEmailConfirmed = !!(window as any).user && !!(window as any).user.confirmed_at;
const makingPublic = ref(false);
const makePublicError = ref('');
const resendLoading = ref(false);
const resendStatus = ref('');

const show = async (link?: string, item?: any, itemType?: 'hike' | 'recording') => {
  copied.value = false;
  makingPublic.value = false;
  makePublicError.value = '';
  resendStatus.value = '';
  currentLink.value = link || props.defaultLink || window.location.href;
  currentItem.value = item || null;
  currentItemType.value = itemType || null;
  itemTypeLabel.value = itemType === 'recording'
    ? t('utils.copy_link_modal.noun_recording')
    : t('utils.copy_link_modal.noun_hike');

  // Determine if item is private
  if (item && itemType === 'hike') {
    isPrivate.value = item.publiclyVisible === false;
  } else if (item && itemType === 'recording') {
    isPrivate.value = item.public === false;
  } else {
    isPrivate.value = false;
  }

  await nextTick();
  if (inputRef.value) {
    inputRef.value.value = currentLink.value;
  }
  modalInstance.value?.show();
};

const hide = () => {
  modalInstance.value?.hide();
};

const copyToClipboard = async () => {
  const text = currentLink.value;
  try {
    await navigator.clipboard.writeText(text);
    copied.value = true;
  } catch (error) {
    if (inputRef.value) {
      inputRef.value.select();
      document.execCommand('copy');
      copied.value = true;
    }
  }
};

async function makePublicAndShare() {
  if (!currentItem.value || !currentItemType.value) return;
  makingPublic.value = true;
  makePublicError.value = '';

  try {
    if (currentItemType.value === 'hike') {
      const result = await graph_client.request(UpdateHikeDetails, {
        input: {
          id: currentItem.value.id,
          publiclyVisible: true,
        },
      });
      const errors = result?.updateHikeDetails?.errors;
      if (errors && errors.length > 0) {
        const msgs = errors.flatMap((e: any) => e.errors || []);
        throw new Error(msgs.join(', ') || t('utils.copy_link_modal.failed_to_update_hike'));
      }
      currentItem.value.publiclyVisible = true;
    } else {
      await graph_client.request(UpdateRecording, {
        input: {
          id: currentItem.value.id,
          public: true,
        },
      });
      currentItem.value.public = true;
    }
    isPrivate.value = false;
  } catch (error: any) {
    makePublicError.value = error?.message || t('utils.copy_link_modal.something_went_wrong');
  } finally {
    makingPublic.value = false;
  }
}

async function resendConfirmationEmail() {
  if (resendLoading.value) return;
  resendLoading.value = true;
  resendStatus.value = '';
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const response = await fetch('/user/resend_confirmation', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken || '', 'Accept': 'application/json' },
    });
    const data = await response.json();
    resendStatus.value = data?.message || t('utils.copy_link_modal.confirmation_email_sent');
  } catch {
    resendStatus.value = t('utils.copy_link_modal.could_not_send_confirmation_email');
  } finally {
    resendLoading.value = false;
  }
}

defineExpose({ show, hide });

onMounted(() => {
  if (modalRef.value) {
    modalInstance.value = Modal.getOrCreateInstance(modalRef.value);
    modalRef.value.addEventListener('hidden.bs.modal', () => {
      copied.value = false;
    });
  }
});

onBeforeUnmount(() => {
  modalInstance.value?.hide();
  modalInstance.value?.dispose?.();
  modalInstance.value = null;
});

const modalId = props.modalId || 'shareLinkModal';
const title = props.title || t('shared.share');
const label = props.label || t('utils.copy_link_modal.share_link');
const helperText = props.helperText || t('utils.copy_link_modal.copy_and_share_this_link');

</script>
